// pages/PublicPages.jsx — fully responsive + complete i18n via useLang()
import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Badge,
  Input,
  Modal,
  Icons,
  PageHeader,
} from "../components/UI";
import { useLang } from "../App";
import { useAsync, useDebounce } from "../hooks";
import {
  productService,
  categoryService,
  inquiryService,
  uploadService,
  statsService,
} from "../services/api";
import { TESTIMONIALS } from "../utils/data";
import cfg from "../config";

// ─── Skeleton ────────────────────────────────────────────────────────────────
const Skeleton = ({
  h = 280,
  n = 4,
  cols = "repeat(auto-fill,minmax(220px,1fr))",
}) => (
  <div style={{ display: "grid", gridTemplateColumns: cols, gap: 18 }}>
    {[...Array(n)].map((_, i) => (
      <div
        key={i}
        style={{
          borderRadius: 14,
          background: "var(--foam)",
          height: h,
          animation: `pulse 1.4s ease-in-out ${i * 0.1}s infinite`,
        }}
      />
    ))}
  </div>
);

// ─── Error banner ─────────────────────────────────────────────────────────────
const ErrBox = ({ msg, onRetry }) => (
  <div
    style={{
      background: "#fff3f3",
      border: "1px solid #fca5a5",
      borderRadius: 12,
      padding: "14px 18px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 24,
    }}
  >
    <span style={{ fontSize: 22 }}>⚠️</span>
    <p style={{ flex: 1, fontSize: 14, color: "#991b1b", lineHeight: 1.5 }}>
      {msg}
    </p>
    {onRetry && (
      <button
        onClick={onRetry}
        style={{
          background: "#dc2626",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "7px 14px",
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 700,
          fontFamily: "var(--sans)",
          whiteSpace: "nowrap",
        }}
      >
        Retry
      </button>
    )}
  </div>
);

// ─── Section header ───────────────────────────────────────────────────────────
const SecHead = ({ eyebrow, title, subtitle, center, light }) => (
  <div style={{ textAlign: center ? "center" : "left", marginBottom: 44 }}>
    {eyebrow && (
      <div
        className="eyebrow"
        style={{
          color: light ? "var(--mint)" : "var(--sage)",
          justifyContent: center ? "center" : "flex-start",
        }}
      >
        {eyebrow}
      </div>
    )}
    <h2 className="h-xl" style={{ color: light ? "#fff" : "var(--ink)" }}>
      {title}
    </h2>
    {subtitle && (
      <p
        style={{
          fontSize: "clamp(15px,1.4vw,17px)",
          color: light ? "rgba(255,255,255,.6)" : "var(--stone)",
          marginTop: 10,
          fontWeight: 300,
          maxWidth: center ? 560 : "none",
          marginInline: center ? "auto" : "unset",
        }}
      >
        {subtitle}
      </p>
    )}
    <div
      className="divider"
      style={{
        marginInline: center ? "auto" : "unset",
        background: light
          ? "linear-gradient(90deg,var(--sage),var(--amber))"
          : undefined,
      }}
    />
  </div>
);

// ─── Product Card ─────────────────────────────────────────────────────────────
const ProductCard = ({ p, cats = [], setPage, setProduct }) => {
  const { lang, t } = useLang();
  const cat = cats.find((c) => c.slug === p.cat);
  const stockColor =
    p.stock === "In Stock" ? "green" : p.stock === "Limited" ? "amber" : "red";
  const stockLabel =
    p.stock === "In Stock"
      ? t("inStock")
      : p.stock === "Limited"
        ? t("limited")
        : t("outOfStock");
  const go = () => {
    setProduct(p);
    setPage("product-detail");
  };

  return (
    <div className="card" style={{ cursor: "pointer" }} onClick={go}>
      {/* Image */}
      <div
        style={{
          height: 180,
          overflow: "hidden",
          position: "relative",
          background: `linear-gradient(155deg,${p.bg || "#e8f5e9"},${p.bg || "#e8f5e9"}bb)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 68,
        }}
      >
        {p.imageUrl ? (
          <img
            src={uploadService.getUrl(p.imageUrl)}
            alt={p.name.en}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span style={{ filter: "drop-shadow(0 6px 12px rgba(0,0,0,.12))" }}>
            {p.emoji || "🌱"}
          </span>
        )}
        <div style={{ position: "absolute", top: 10, right: 10 }}>
          <Badge color={stockColor} dot>
            {stockLabel}
          </Badge>
        </div>
        {p.featured && (
          <div
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              background: "var(--honey)",
              color: "var(--forest)",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              padding: "3px 8px",
              borderRadius: 4,
            }}
          >
            ★ Featured
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "16px 16px 18px" }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1.8,
            textTransform: "uppercase",
            color: "var(--sage)",
            marginBottom: 5,
          }}
        >
          {cat?.name[lang] || cat?.name?.en || "—"}
        </div>
        <h3
          style={{
            fontFamily: "var(--serif)",
            fontSize: 16,
            color: "var(--ink)",
            marginBottom: 6,
            lineHeight: 1.25,
            letterSpacing: "-.01em",
          }}
        >
          {p.name[lang] || p.name.en || "—"}
        </h3>
        <p
          style={{
            fontSize: 12,
            color: "var(--stone)",
            lineHeight: 1.65,
            marginBottom: 14,
            height: 38,
            overflow: "hidden",
          }}
        >
          {p.desc[lang] || p.desc.en || ""}
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{
              fontFamily: "var(--serif)",
              fontSize: 20,
              color: "var(--pine)",
            }}
          >
            ₹{p.price}
            <span
              style={{
                fontSize: 11,
                color: "var(--stone)",
                fontFamily: "var(--sans)",
              }}
            >
              {" "}
              {t("perPacket")}
            </span>
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              go();
            }}
            style={{
              background: "var(--foam)",
              color: "var(--pine)",
              border: "none",
              borderRadius: 8,
              padding: "7px 12px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--sans)",
              display: "flex",
              alignItems: "center",
              gap: 5,
              transition: "all .2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--emerald)";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--foam)";
              e.currentTarget.style.color = "var(--pine)";
            }}
          >
            <Icons.Arrow size={13} /> {t("viewDetails")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// HOME PAGE
// ═════════════════════════════════════════════════════════════════════════════
export const HomePage = ({ setPage, setProduct }) => {
  const { lang, t } = useLang();
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);
  const [testi, setTesti] = useState([]);
  const [loadFeat, setLoadFeat] = useState(true);
  const [loadCat, setLoadCat] = useState(true);
  const [errFeat, setErrFeat] = useState(null);
  const [errCat, setErrCat] = useState(null);
  // Live stats from API
  const [siteStats, setSiteStats] = useState(null);

  const fetchFeatured = useCallback(() => {
    setLoadFeat(true);
    setErrFeat(null);
    productService
      .getFeatured()
      .then(setFeatured)
      .catch((e) => setErrFeat(e.message))
      .finally(() => setLoadFeat(false));
  }, []);
  const fetchCats = useCallback(() => {
    setLoadCat(true);
    setErrCat(null);
    categoryService
      .getAll()
      .then(setCats)
      .catch((e) => setErrCat(e.message))
      .finally(() => setLoadCat(false));
  }, []);
  const fetchStats = useCallback(() => {
    statsService
      .getPublic()
      .then(setSiteStats)
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchFeatured();
    fetchCats();
    fetchStats();
    setTesti(TESTIMONIALS);
  }, [fetchFeatured, fetchCats, fetchStats]);

  // Dynamic stats — live from API, fallback to sensible defaults while loading
  const STATS = [
    [siteStats ? `${siteStats.yearsInBusiness}+` : "30+", "yearsExp"],
    [
      siteStats ? `${siteStats.happyFarmers.toLocaleString()}+` : "5,000+",
      "happyFarmers",
    ],
    [siteStats ? `${siteStats.productCount}+` : "200+", "seedVarieties"],
    [siteStats ? `${siteStats.citiesServed}+` : "50+", "cities"],
  ];
  const BENEFITS = [
    { icon: "🏅", t1: "benefit1", t2: "benefit1d" },
    { icon: "🌿", t1: "benefit2", t2: "benefit2d" },
    { icon: "🚚", t1: "benefit3", t2: "benefit3d" },
    { icon: "👨‍🌾", t1: "benefit4", t2: "benefit4d" },
  ];
  const TRUST = [
    "✓ Certified Seeds",
    "🌾 30+ Years",
    "🚚 Fast Delivery",
    "🌿 200+ Varieties",
    "👨‍🌾 5K+ Farmers",
    "📋 Bulk Orders",
    "🏅 Govt Approved",
    "💯 Genuine Seeds",
  ];

  // Hero title changes with language
  const heroTitle = {
    en: (
      <>
        <em style={{ color: "var(--amber)", fontStyle: "italic" }}>
          Nourishing
        </em>
        <br />
        Gujarat Since {cfg.established}
      </>
    ),
    hi: (
      <>
        बीज जो गुजरात को
        <br />
        <em style={{ color: "var(--amber)", fontStyle: "italic" }}>
          पोषित
        </em>{" "}
        करते हैं
      </>
    ),
    gu: (
      <>
        ગુજરાતને
        <br />
        <em style={{ color: "var(--amber)", fontStyle: "italic" }}>
          પોષણ
        </em>{" "}
        આપતા બીજ
      </>
    ),
  };

  return (
    <div>
      {/* ══ HERO ════════════════════════════════════════════════════════════ */}
      <section
        style={{
          position: "relative",
          minHeight: "100vh",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          background: "var(--forest)",
        }}
      >
        {/* Decorative layers */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(ellipse 80% 60% at 70% 40%,rgba(30,122,60,.35),transparent 70%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.04,
            pointerEvents: "none",
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div
          className="wrap"
          style={{
            position: "relative",
            zIndex: 1,
            paddingTop: "calc(var(--nav-h) + 48px)",
            paddingBottom: 72,
            width: "100%",
          }}
        >
          {/* Responsive hero grid: 2 cols desktop, 1 col mobile */}
          <div className="rg-2 hero-grid">
            {/* Left: copy */}
            <div>
              {/* EST badge */}
              <div
                className="fade-up"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: "rgba(255,255,255,.06)",
                  border: "1px solid rgba(255,255,255,.12)",
                  borderRadius: 50,
                  padding: "6px 14px 6px 8px",
                  marginBottom: 26,
                }}
              >
                <span
                  style={{
                    background: "var(--honey)",
                    borderRadius: 50,
                    padding: "2px 10px",
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--forest)",
                    letterSpacing: 1,
                  }}
                >
                  EST. {cfg.established}
                </span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,.65)" }}>
                  {cfg.heroLocation}
                </span>
              </div>

              {/* H1 — language aware */}
              <h1
                className="h-display fade-up d1"
                style={{ color: "#fff", marginBottom: 14 }}
              >
                {heroTitle[lang]}
              </h1>

              <p
                className="fade-up d2"
                style={{
                  fontFamily: "var(--serif)",
                  fontSize: "clamp(15px,1.8vw,20px)",
                  color: "var(--mint)",
                  fontStyle: "italic",
                  marginBottom: 16,
                }}
              >
                {t("heroTagline")}
              </p>
              <p
                className="fade-up d2 lead"
                style={{
                  maxWidth: 440,
                  marginBottom: 32,
                  color: "rgba(255,255,255,.62)",
                }}
              >
                {t("heroDesc")}
              </p>

              <div
                className="fade-up d3"
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  marginBottom: 40,
                }}
              >
                <Button
                  variant="gold"
                  size="lg"
                  leftIcon={<Icons.Leaf size={16} />}
                  onClick={() => setPage("products")}
                >
                  {t("shopNow")}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setPage("contact")}
                  style={{
                    background: "rgba(255,255,255,.08)",
                    color: "#fff",
                    border: "1.5px solid rgba(255,255,255,.2)",
                  }}
                >
                  {t("contactUs")}
                </Button>
              </div>

              {/* Stats — language aware labels */}
              <div
                className="fade-up d4"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  borderTop: "1px solid rgba(255,255,255,.1)",
                  paddingTop: 24,
                }}
              >
                {STATS.map(([v, k], i) => (
                  <div
                    key={k}
                    style={{
                      flex: "1 1 80px",
                      textAlign: i === 0 ? "left" : "center",
                      paddingRight: 16,
                      paddingLeft: i > 0 ? 16 : 0,
                      borderRight:
                        i < 3 ? "1px solid rgba(255,255,255,.08)" : "none",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--serif)",
                        fontSize: "clamp(20px,2.8vw,30px)",
                        color: "#fff",
                        lineHeight: 1,
                      }}
                    >
                      {v}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "rgba(255,255,255,.42)",
                        marginTop: 4,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(k)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: featured product preview — hidden on mobile via .hero-grid CSS */}
            <div className="scale-in d2" style={{ position: "relative" }}>
              <div
                style={{
                  background: "rgba(255,255,255,.04)",
                  border: "1px solid rgba(255,255,255,.1)",
                  borderRadius: 22,
                  padding: 24,
                  backdropFilter: "blur(20px)",
                  boxShadow: "0 40px 80px rgba(0,0,0,.3)",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                  }}
                >
                  {loadFeat
                    ? [...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          style={{
                            borderRadius: 10,
                            background: "rgba(255,255,255,.06)",
                            height: 90,
                            animation: "pulse 1.4s ease-in-out infinite",
                          }}
                        />
                      ))
                    : featured.slice(0, 4).map((p) => (
                        <div
                          key={p.id}
                          style={{
                            background: "rgba(255,255,255,.06)",
                            borderRadius: 12,
                            padding: "12px 10px",
                            border: "1px solid rgba(255,255,255,.08)",
                            textAlign: "center",
                            transition: "background .25s",
                            overflow: "hidden",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setProduct(p);
                            setPage("product-detail");
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255,255,255,.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255,255,255,.06)";
                          }}
                        >
                          {p.imageUrl ? (
                            <img
                              src={uploadService.getUrl(p.imageUrl)}
                              alt={p.name.en}
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: 8,
                                objectFit: "cover",
                                marginBottom: 7,
                                margin: "0 auto 7px",
                              }}
                            />
                          ) : (
                            <div style={{ fontSize: 30, marginBottom: 7 }}>
                              {p.emoji || "🌱"}
                            </div>
                          )}
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "rgba(255,255,255,.85)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {p.name[lang] || p.name.en || ""}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "var(--amber)",
                              marginTop: 3,
                            }}
                          >
                            ₹{p.price}
                          </div>
                        </div>
                      ))}
                </div>
                <div
                  style={{
                    marginTop: 12,
                    padding: "11px 14px",
                    background: "rgba(212,146,10,.12)",
                    borderRadius: 10,
                    border: "1px solid rgba(212,146,10,.2)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{ color: "rgba(255,255,255,.65)", fontSize: 13 }}
                  >
                    {featured.length}+ {t("seedVarieties").toLowerCase()}
                  </span>
                  <button
                    onClick={() => setPage("products")}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--amber)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "var(--sans)",
                    }}
                  >
                    {t("viewAll")} →
                  </button>
                </div>
              </div>
              <div
                style={{
                  position: "absolute",
                  top: -16,
                  right: -12,
                  background: "var(--honey)",
                  color: "var(--forest)",
                  padding: "8px 16px",
                  borderRadius: 50,
                  fontSize: 12,
                  fontWeight: 700,
                  boxShadow: "0 8px 24px rgba(212,146,10,.45)",
                  animation: "float 5s ease-in-out infinite",
                  whiteSpace: "nowrap",
                }}
              >
                ✓ Quality Certified
              </div>
            </div>
          </div>
        </div>

        {/* Wave */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0 }}>
          <svg
            viewBox="0 0 1440 48"
            preserveAspectRatio="none"
            style={{ width: "100%", height: 48, display: "block" }}
          >
            <path
              d="M0,0 C360,48 1080,48 1440,0 L1440,48 L0,48 Z"
              fill="#fff"
            />
          </svg>
        </div>
      </section>

      {/* ══ TRUST MARQUEE ══════════════════════════════════════════════════ */}
      <div
        style={{
          background: "var(--forest)",
          padding: "10px 0",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            width: "max-content",
            animation: "marquee 30s linear infinite",
          }}
        >
          {[...TRUST, ...TRUST].map((item, i) => (
            <span
              key={i}
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "rgba(255,255,255,.55)",
                padding: "0 24px",
                whiteSpace: "nowrap",
                borderRight: "1px solid rgba(255,255,255,.1)",
              }}
            >
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ══ CATEGORIES ═════════════════════════════════════════════════════ */}
      <section className="section" style={{ background: "#fff" }}>
        <div className="wrap">
          <SecHead eyebrow={t("catsTitle")} title={t("catsSub")} center />
          {errCat && <ErrBox msg={errCat} onRetry={fetchCats} />}
          {loadCat ? (
            <Skeleton
              h={150}
              n={5}
              cols="repeat(auto-fill,minmax(160px,1fr))"
            />
          ) : (
            <div className="rg-auto-cat">
              {cats.map((c, i) => {
                const GRADS = [
                  ["#1a5c2e", "#2d8a45"],
                  ["#7c2d12", "#ea580c"],
                  ["#701a75", "#a21caf"],
                  ["#713f12", "#ca8a04"],
                  ["#14532d", "#16a34a"],
                  ["#1e3a5f", "#2563eb"],
                ];
                const [from, to] =
                  c.from && c.to ? [c.from, c.to] : GRADS[i % GRADS.length];
                return (
                  <div
                    key={c.id}
                    onClick={() => setPage("products")}
                    style={{
                      background: `linear-gradient(160deg,${from},${to})`,
                      borderRadius: 18,
                      padding: 20,
                      textAlign: "center",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      transition:
                        "transform .3s var(--ease), box-shadow .3s var(--ease)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-5px)";
                      e.currentTarget.style.boxShadow =
                        "0 20px 40px rgba(0,0,0,.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "";
                      e.currentTarget.style.boxShadow = "";
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        backgroundImage:
                          "radial-gradient(circle at 80% 20%,rgba(255,255,255,.12),transparent 60%)",
                      }}
                    />
                    {c.imageUrl ? (
                      <img
                        src={uploadService.getUrl(c.imageUrl)}
                        alt={c.name.en}
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 10,
                          objectFit: "cover",
                          margin: "0 auto 10px",
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: 40, marginBottom: 10 }}>
                        {c.icon}
                      </div>
                    )}
                    {/* Category name switches language instantly */}
                    <h3
                      style={{
                        fontFamily: "var(--sans)",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "rgba(255,255,255,.95)",
                        margin: "0 0 4px",
                      }}
                    >
                      {c.name[lang] || c.name.en}
                    </h3>
                    <p
                      style={{
                        fontSize: 11,
                        color: "rgba(255,255,255,.55)",
                        margin: 0,
                      }}
                    >
                      {c.count} {t("seedVarieties").toLowerCase()}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ══ FEATURED PRODUCTS ══════════════════════════════════════════════ */}
      <section className="section" style={{ background: "var(--snow)" }}>
        <div className="wrap">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 16,
              marginBottom: 44,
            }}
          >
            <SecHead eyebrow={t("featuredTitle")} title={t("featuredSub")} />
            <button
              onClick={() => setPage("products")}
              style={{
                background: "var(--foam)",
                color: "var(--pine)",
                border: "none",
                borderRadius: 10,
                padding: "9px 18px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "var(--sans)",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all .2s",
                marginBottom: 44,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--emerald)";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--foam)";
                e.currentTarget.style.color = "var(--pine)";
              }}
            >
              {t("viewAll")} <Icons.Arrow size={14} />
            </button>
          </div>
          {errFeat && <ErrBox msg={errFeat} onRetry={fetchFeatured} />}
          {loadFeat ? (
            <Skeleton h={300} n={4} />
          ) : (
            <div className="rg-auto-prod">
              {featured.map((p) => (
                <ProductCard
                  key={p.id}
                  p={p}
                  cats={cats}
                  setPage={setPage}
                  setProduct={setProduct}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ══ WHY US ═════════════════════════════════════════════════════════ */}
      <section className="section" style={{ background: "#fff" }}>
        <div className="wrap">
          <div className="rg-2">
            {/* Left */}
            <div>
              <SecHead eyebrow={t("whyUs")} title={t("whyUsSub")} />
              <p className="lead" style={{ marginBottom: 28 }}>
                {t("aboutDesc")}
              </p>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                {(siteStats
                  ? [
                      [`${siteStats.yearsInBusiness}+`, "yearsExp"],
                      [
                        `${siteStats.happyFarmers.toLocaleString()}+`,
                        "happyFarmers",
                      ],
                    ]
                  : [
                      ["30+", "yearsExp"],
                      ["5,000+", "happyFarmers"],
                    ]
                ).map(([v, k]) => (
                  <div
                    key={k}
                    style={{
                      padding: "14px 20px",
                      background: "var(--foam)",
                      borderRadius: 12,
                      border: "1px solid var(--mint)",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--serif)",
                        fontSize: 26,
                        color: "var(--pine)",
                      }}
                    >
                      {v}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--stone)",
                        marginTop: 2,
                        fontWeight: 500,
                      }}
                    >
                      {t(k)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Right: 2x2 benefit cards */}
            <div className="rg-benefit">
              {BENEFITS.map((b, i) => (
                <div
                  key={i}
                  className="fade-up"
                  style={{
                    animationDelay: `${i * 0.1}s`,
                    padding: "20px 16px",
                    background: "#fff",
                    border: "1px solid rgba(0,0,0,.07)",
                    borderRadius: 14,
                    boxShadow: "var(--shadow-sm)",
                    transition: "all .3s var(--ease)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "var(--shadow-lg)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 10 }}>{b.icon}</div>
                  {/* Benefit titles switch language */}
                  <h3
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "var(--ink)",
                      marginBottom: 6,
                    }}
                  >
                    {t(b.t1)}
                  </h3>
                  <p
                    style={{
                      fontSize: 12,
                      color: "var(--stone)",
                      lineHeight: 1.65,
                      margin: 0,
                    }}
                  >
                    {t(b.t2)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ═══════════════════════════════════════════════════ */}
      <section
        className="section"
        style={{
          background: "var(--forest)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(circle at 20% 50%,rgba(58,158,88,.1),transparent 60%)",
          }}
        />
        <div className="wrap" style={{ position: "relative" }}>
          <SecHead
            eyebrow={t("testiTitle")}
            title={t("testiSub")}
            center
            light
          />
          <div className="rg-auto-testi">
            {testi.map((tm, i) => (
              <div
                key={tm.id}
                className="fade-up"
                style={{
                  animationDelay: `${i * 0.12}s`,
                  background: "rgba(255,255,255,.04)",
                  border: "1px solid rgba(255,255,255,.08)",
                  borderRadius: 18,
                  padding: "28px",
                  backdropFilter: "blur(10px)",
                  transition: "all .3s var(--ease)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,.07)";
                  e.currentTarget.style.transform = "translateY(-4px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,.04)";
                  e.currentTarget.style.transform = "";
                }}
              >
                <div
                  style={{
                    fontFamily: "Georgia,serif",
                    fontSize: 64,
                    lineHeight: 1,
                    color: "var(--sage)",
                    opacity: 0.35,
                    marginBottom: -12,
                  }}
                >
                  "
                </div>
                {/* Testimonial text switches language */}
                <p
                  style={{
                    fontSize: 14,
                    color: "rgba(255,255,255,.78)",
                    lineHeight: 1.85,
                    fontWeight: 300,
                    marginBottom: 22,
                    fontStyle: "italic",
                  }}
                >
                  {tm.msg[lang]}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg,var(--sage),var(--pine))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "var(--serif)",
                      fontSize: 18,
                      color: "#fff",
                      flexShrink: 0,
                    }}
                  >
                    {tm.name.charAt(0)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{ fontWeight: 600, fontSize: 14, color: "#fff" }}
                    >
                      {tm.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "rgba(255,255,255,.4)",
                        marginTop: 1,
                      }}
                    >
                      {tm.loc}
                    </div>
                  </div>
                  <div>
                    {"★"
                      .repeat(tm.rating)
                      .split("")
                      .map((_, j) => (
                        <span
                          key={j}
                          style={{ color: "var(--amber)", fontSize: 13 }}
                        >
                          ★
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════════════════════ */}
      <section
        className="section-sm"
        style={{
          background: "var(--cream)",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background:
              "radial-gradient(circle,rgba(30,122,60,.06),transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div className="wrap" style={{ position: "relative" }}>
          {/* CTA title switches language */}
          <h2 className="h-xl" style={{ marginBottom: 12 }}>
            {t("ctaTitle")}
          </h2>
          <p className="lead" style={{ maxWidth: 480, margin: "0 auto 36px" }}>
            {t("ctaSub")}
          </p>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="primary"
              size="lg"
              leftIcon={<Icons.Phone size={16} />}
              onClick={() => setPage("contact")}
            >
              {t("getInTouch")}
            </Button>
            <Button
              variant="whatsapp"
              size="lg"
              onClick={() => window.open(`https://wa.me/${cfg.whatsapp}`)}
            >
              💬 WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// ABOUT PAGE
// ═════════════════════════════════════════════════════════════════════════════
export const AboutPage = () => {
  const { t } = useLang();
  // Fetch live stats for AboutPage counters
  const [aboutStats, setAboutStats] = useState(null);
  useEffect(() => {
    statsService
      .getPublic()
      .then(setAboutStats)
      .catch(() => {});
  }, []);
  const STATS = [
    [aboutStats ? `${aboutStats.yearsInBusiness}+` : "30+", "yearsExp"],
    [
      aboutStats ? `${aboutStats.happyFarmers.toLocaleString()}+` : "5,000+",
      "happyFarmers",
    ],
    [aboutStats ? `${aboutStats.productCount}+` : "200+", "seedVarieties"],
    [aboutStats ? `${aboutStats.citiesServed}+` : "50+", "cities"],
  ];

  return (
    <div style={{ paddingBottom: 72 }}>
      <PageHeader
        title={t("aboutTitle")}
        subtitle={t("aboutDesc")}
        crumb={["Home", t("about")]}
      />

      {/* Stats bar */}
      <div style={{ background: "var(--forest)" }}>
        <div className="wrap">
          <div className="rg-4 about-stats">
            {STATS.map(([v, k], i) => (
              <div
                key={k}
                style={{
                  padding: "32px 0",
                  textAlign: "center",
                  borderRight:
                    i < 3 ? "1px solid rgba(255,255,255,.08)" : "none",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--serif)",
                    fontSize: "clamp(28px,3.5vw,40px)",
                    color: "var(--amber)",
                    lineHeight: 1,
                  }}
                >
                  {v}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,.5)",
                    marginTop: 5,
                    fontWeight: 500,
                  }}
                >
                  {t(k)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="wrap" style={{ paddingTop: 72 }}>
        {/* Mission / Vision */}
        <div className="rg-2" style={{ marginBottom: 52 }}>
          {[
            {
              icon: "🎯",
              tK: "mission",
              tT: "missionText",
              bg: "var(--foam)",
              bdr: "var(--mint)",
            },
            {
              icon: "🔭",
              tK: "vision",
              tT: "visionText",
              bg: "var(--cream)",
              bdr: "#f0e0aa",
            },
          ].map((c) => (
            <div
              key={c.tK}
              style={{
                background: c.bg,
                border: `1px solid ${c.bdr}`,
                borderRadius: 18,
                padding: "32px 28px",
              }}
            >
              <div style={{ fontSize: 38, marginBottom: 14 }}>{c.icon}</div>
              <h3 className="h-md" style={{ marginBottom: 10 }}>
                {t(c.tK)}
              </h3>
              <p
                style={{
                  fontSize: 15,
                  color: "var(--stone)",
                  lineHeight: 1.85,
                  fontWeight: 300,
                }}
              >
                {t(c.tT)}
              </p>
            </div>
          ))}
        </div>

        {/* Quality block */}
        <div
          style={{
            background: "var(--forest)",
            borderRadius: 22,
            padding: "clamp(28px,4vw,52px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              backgroundImage:
                "radial-gradient(circle at 70% 50%,rgba(58,158,88,.15),transparent 60%)",
            }}
          />
          <div className="rg-2" style={{ position: "relative" }}>
            <div>
              <div className="eyebrow" style={{ color: "var(--mint)" }}>
                {t("quality")}
              </div>
              <h3 className="h-lg" style={{ color: "#fff", marginBottom: 12 }}>
                Every seed, tested &amp; certified
              </h3>
              <p
                style={{
                  color: "rgba(255,255,255,.6)",
                  lineHeight: 1.85,
                  fontSize: 15,
                  fontWeight: 300,
                  marginBottom: 24,
                }}
              >
                {t("qualityText")}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {[
                  "Germination Test",
                  "Purity Analysis",
                  "Field Trials",
                  "Govt. Certified",
                ].map((item) => (
                  <div
                    key={item}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      background: "rgba(255,255,255,.08)",
                      border: "1px solid rgba(255,255,255,.12)",
                      borderRadius: 50,
                      padding: "5px 14px",
                      fontSize: 12,
                      color: "rgba(255,255,255,.8)",
                      fontWeight: 500,
                    }}
                  >
                    <span style={{ color: "var(--mint)" }}>
                      <Icons.Check size={12} />
                    </span>{" "}
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div
              style={{
                textAlign: "center",
                fontSize: 110,
                opacity: 0.12,
                lineHeight: 1,
              }}
            >
              🏅
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// PRODUCTS PAGE
// ═════════════════════════════════════════════════════════════════════════════
export const ProductsPage = ({ setPage, setProduct }) => {
  const { lang, t } = useLang();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  const [srch, setSrch] = useState("");
  const [sort, setSort] = useState("default");
  const dSrch = useDebounce(srch, 280);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([productService.getAll(), categoryService.getAll()])
      .then(([p, c]) => {
        setProducts(p);
        setCats(c);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  let list = products.filter(
    (p) =>
      (activeCat === "all" || p.cat === activeCat) &&
      (!dSrch ||
        (p.name[lang] || p.name.en || "")
          .toLowerCase()
          .includes(dSrch.toLowerCase())),
  );
  if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
  if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);

  return (
    <div style={{ paddingBottom: 72 }}>
      <PageHeader
        title={t("products")}
        subtitle="Browse our complete collection of premium quality seeds"
        crumb={["Home", t("products")]}
      />

      <div className="wrap" style={{ paddingTop: 44 }}>
        {error && <ErrBox msg={error} onRetry={fetchAll} />}

        {/* Filter bar */}
        <div
          style={{
            background: "#fff",
            border: "1px solid rgba(0,0,0,.07)",
            borderRadius: 14,
            padding: "clamp(12px,2vw,18px) clamp(12px,2vw,20px)",
            marginBottom: 28,
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div style={{ flex: 1, minWidth: 160, position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: 13,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--stone)",
                display: "flex",
                pointerEvents: "none",
              }}
            >
              <Icons.Search size={14} />
            </span>
            <input
              value={srch}
              onChange={(e) => setSrch(e.target.value)}
              placeholder={t("searchPlaceholder")}
              className="field"
              style={{ paddingLeft: 40 }}
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="field"
            style={{ width: "auto", minWidth: 160 }}
          >
            <option value="default">{t("sortBy")}</option>
            <option value="price-asc">{t("priceAsc")}</option>
            <option value="price-desc">{t("priceDesc")}</option>
          </select>
          <span
            style={{
              fontSize: 13,
              color: "var(--stone)",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            <strong style={{ color: "var(--pine)" }}>{list.length}</strong>{" "}
            {t("results") || "results"}
          </span>
        </div>

        {/* Category chips — names switch language */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 32,
            flexWrap: "wrap",
          }}
        >
          {[
            {
              slug: "all",
              name: { en: t("allCats"), hi: t("allCats"), gu: t("allCats") },
              icon: "🌱",
            },
            ...cats,
          ].map((c) => {
            const active = activeCat === c.slug;
            return (
              <button
                key={c.slug}
                onClick={() => setActiveCat(c.slug)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 50,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "var(--sans)",
                  fontWeight: 600,
                  fontSize: 13,
                  background: active ? "var(--forest)" : "var(--foam)",
                  color: active ? "#fff" : "var(--stone)",
                  boxShadow: active ? "0 4px 14px rgba(15,61,26,.25)" : "none",
                  transition: "all .2s",
                }}
              >
                <span>{c.icon}</span> {c.name[lang] || c.name.en}
              </button>
            );
          })}
        </div>

        {/* Product grid — responsive via CSS class */}
        {loading ? (
          <Skeleton h={300} n={6} />
        ) : list.length === 0 ? (
          <div style={{ textAlign: "center", padding: "72px 20px" }}>
            <div style={{ fontSize: 52, marginBottom: 14 }}>🌱</div>
            <p
              style={{
                fontSize: 16,
                color: "var(--stone)",
                fontWeight: 300,
                marginBottom: 18,
              }}
            >
              {t("noProducts")}
            </p>
            <Button
              variant="outline"
              size="md"
              onClick={() => {
                setSrch("");
                setActiveCat("all");
              }}
            >
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="rg-auto-prod">
            {list.map((p) => (
              <ProductCard
                key={p.id}
                p={p}
                cats={cats}
                setPage={setPage}
                setProduct={setProduct}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// PRODUCT DETAIL PAGE
// ═════════════════════════════════════════════════════════════════════════════
export const ProductDetailPage = ({ product, setPage, setProduct }) => {
  const { lang, t } = useLang();
  const [tab, setTab] = useState("desc");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const {
    execute: submitInq,
    loading: submitting,
    data: submitDone,
  } = useAsync(inquiryService.submit);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    categoryService
      .getAll()
      .then(setCats)
      .catch(() => {});
  }, []);

  if (!product) return null;
  const cat = cats.find((c) => c.slug === product.cat);

  const hc = (f) => (ev) => {
    setForm((p) => ({ ...p, [f]: ev.target.value }));
    if (errors[f]) setErrors((p) => ({ ...p, [f]: undefined }));
  };
  const handleSubmit = async () => {
    const e = {};
    if (!form.name.trim()) e.name = t("name") + " required";
    if (!form.phone.trim()) e.phone = t("phone") + " required";
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    await submitInq({
      name: form.name,
      phone: form.phone,
      email: form.email,
      message: form.message,
      productId: product.id,
      productName: product.name.en,
    });
  };

  return (
    <div style={{ paddingBottom: 72 }}>
      <PageHeader
        title={product.name[lang] || product.name.en}
        crumb={[t("products"), cat?.name[lang] || cat?.name?.en]}
      />

      <div className="wrap" style={{ paddingTop: 48 }}>
        {/* Main layout: image + info — responsive */}
        <div className="rg-2" style={{ marginBottom: 52 }}>
          {/* Image */}
          <div
            style={{
              aspectRatio: "4/3",
              background: `linear-gradient(160deg,${product.bg || "#e8f5e9"},${product.bg || "#e8f5e9"}dd)`,
              borderRadius: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 100,
              position: "relative",
              overflow: "hidden",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            {product.imageUrl ? (
              <img
                src={uploadService.getUrl(product.imageUrl)}
                alt={product.name.en}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <span
                style={{
                  animation: "float 6s ease-in-out infinite",
                  filter: "drop-shadow(0 12px 24px rgba(0,0,0,.14))",
                }}
              >
                {product.emoji || "🌱"}
              </span>
            )}
          </div>

          {/* Info */}
          <div>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 14,
              }}
            >
              <Badge
                color={
                  product.stock === "In Stock"
                    ? "green"
                    : product.stock === "Limited"
                      ? "amber"
                      : "red"
                }
                dot
              >
                {product.stock === "In Stock"
                  ? t("inStock")
                  : product.stock === "Limited"
                    ? t("limited")
                    : t("outOfStock")}
              </Badge>
              {product.featured && <Badge color="amber">Featured</Badge>}
            </div>

            {/* Product name switches language */}
            <h1
              style={{
                fontFamily: "var(--serif)",
                fontSize: "clamp(24px,3.5vw,40px)",
                color: "var(--ink)",
                marginBottom: 10,
                lineHeight: 1.1,
              }}
            >
              {product.name[lang] || product.name.en}
            </h1>
            {/* Description switches language */}
            <p className="lead" style={{ marginBottom: 24 }}>
              {product.desc[lang] || product.desc.en}
            </p>

            {/* Meta grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginBottom: 24,
              }}
            >
              {[
                [
                  t("seedType"),
                  product.type?.[lang] || product.type?.en || "—",
                ],
                [
                  t("sku"),
                  `${cfg.skuPrefix}-${String(product.id).padStart(3, "0")}`,
                ],
                [t("category"), cat?.name[lang] || cat?.name?.en || "—"],
                [t("season"), t("allSeason")],
              ].map(([l, v]) => (
                <div
                  key={l}
                  style={{
                    background: "var(--foam)",
                    borderRadius: 10,
                    padding: "10px 14px",
                    border: "1px solid var(--mint)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                      color: "var(--stone)",
                      marginBottom: 3,
                    }}
                  >
                    {l}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "var(--pine)",
                    }}
                  >
                    {v}
                  </div>
                </div>
              ))}
            </div>

            {/* Price */}
            <div
              style={{
                background: "var(--forest)",
                borderRadius: 14,
                padding: "18px 22px",
                marginBottom: 20,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,.45)",
                    marginBottom: 6,
                  }}
                >
                  {t("pricePerPacket") || "Price per packet"}
                </div>
                <div
                  style={{
                    fontFamily: "var(--serif)",
                    fontSize: 40,
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  ₹{product.price}
                </div>
              </div>
              <div style={{ fontSize: 50, opacity: 0.15 }}>
                {product.emoji || "🌱"}
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Button
                variant="primary"
                size="lg"
                leftIcon={<Icons.Mail size={16} />}
                onClick={() => setOpen(true)}
              >
                {t("sendInquiry")}
              </Button>
              <Button
                variant="whatsapp"
                size="lg"
                onClick={() =>
                  window.open(
                    `https://wa.me/${cfg.whatsapp}?text=${encodeURIComponent("Interested in: " + product.name.en)}`,
                  )
                }
              >
                💬 WhatsApp
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ marginBottom: 52 }}>
          <div
            style={{
              display: "flex",
              borderBottom: "2px solid var(--foam)",
              marginBottom: 20,
            }}
          >
            {[
              ["desc", t("aboutDesc") || "Description"],
              ["grow", t("growing")],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                style={{
                  padding: "12px 20px",
                  border: "none",
                  cursor: "pointer",
                  background: "none",
                  fontFamily: "var(--sans)",
                  fontSize: 14,
                  fontWeight: 600,
                  color: tab === k ? "var(--pine)" : "var(--stone)",
                  borderBottom: `2px solid ${tab === k ? "var(--emerald)" : "transparent"}`,
                  marginBottom: -2,
                  transition: "all .2s",
                }}
              >
                {k === "desc"
                  ? lang === "hi"
                    ? "विवरण"
                    : lang === "gu"
                      ? "વર્ણન"
                      : "Description"
                  : l}
              </button>
            ))}
          </div>
          <div
            style={{
              background: "var(--foam)",
              borderRadius: 14,
              padding: "20px 24px",
              fontSize: 15,
              lineHeight: 1.9,
              color: "var(--stone)",
            }}
          >
            {tab === "desc"
              ? product.desc[lang] || product.desc.en || "—"
              : {
                  en: "Sow in well-prepared seed beds. Maintain proper row spacing and regular irrigation.",
                  hi: "अच्छी तरह तैयार बेड में बोएं। उचित दूरी और सिंचाई बनाए रखें।",
                  gu: "સારી રીતે તૈયાર ક્યારીઓમાં વાવો. યોગ્ય અંતર અને સિંચાઈ જાળવો.",
                }[lang]}
          </div>
        </div>
      </div>

      {/* Inquiry modal */}
      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={t("sendInquiry")}
        subtitle={`Re: ${product.name[lang] || product.name.en}`}
        footer={
          !submitDone && (
            <Button
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting}
              onClick={handleSubmit}
            >
              {t("send")} <Icons.Arrow size={15} />
            </Button>
          )
        }
      >
        {submitDone ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "#dcfce7",
                border: "2px solid #bbf7d0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                color: "#16a34a",
              }}
            >
              <Icons.Check size={26} />
            </div>
            <h4
              style={{
                fontFamily: "var(--serif)",
                fontSize: 22,
                color: "var(--pine)",
                marginBottom: 8,
              }}
            >
              {lang === "hi"
                ? "पूछताछ भेज दी!"
                : lang === "gu"
                  ? "પૂછપરછ મોકલાઈ!"
                  : "Inquiry Sent!"}
            </h4>
            <p style={{ color: "var(--stone)", fontSize: 14 }}>
              {t("msgSent")}
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <Input
                label={t("name")}
                name="n"
                value={form.name}
                onChange={hc("name")}
                required
                error={errors.name}
              />
              <Input
                label={t("phone")}
                name="p"
                value={form.phone}
                onChange={hc("phone")}
                type="tel"
                required
                error={errors.phone}
              />
            </div>
            <Input
              label={t("email")}
              name="e"
              value={form.email}
              onChange={hc("email")}
              type="email"
            />
            <Input
              as="textarea"
              label={t("message")}
              name="m"
              value={form.message}
              onChange={hc("message")}
              rows={3}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// CONTACT PAGE
// ═════════════════════════════════════════════════════════════════════════════
export const ContactPage = () => {
  const { lang, t } = useLang();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const {
    execute: submit,
    loading,
    data: done,
  } = useAsync(inquiryService.submit);

  const hc = (f) => (e) => {
    setForm((p) => ({ ...p, [f]: e.target.value }));
    if (errors[f]) setErrors((p) => ({ ...p, [f]: undefined }));
  };
  const handleSubmit = async () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.phone.trim()) e.phone = "Required";
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    await submit({
      name: form.name,
      phone: form.phone,
      email: form.email,
      message: form.message,
    });
  };

  const INFO = [
    { icon: <Icons.Pin size={15} />, label: t("address"), val: t("addr") },
    { icon: <Icons.Phone size={15} />, label: t("phone"), val: t("tel") },
    { icon: <Icons.Mail size={15} />, label: t("email"), val: t("mail") },
    { icon: <Icons.Clock size={15} />, label: t("biz"), val: t("hours") },
  ];

  return (
    <div style={{ paddingBottom: 72 }}>
      <PageHeader
        title={t("contactTitle")}
        subtitle={
          lang === "hi"
            ? "हम ऑर्डर, पूछताछ और सलाह में मदद करने के लिए यहाँ हैं।"
            : lang === "gu"
              ? "અમે ઓર્ડર, પૂછપરછ અને સલાહ માટે અહીં છીએ."
              : "We're here to help with orders, inquiries and expert advice."
        }
        crumb={["Home", t("contact")]}
      />

      <div className="wrap" style={{ paddingTop: 56 }}>
        <div className="rg-2 grid-1">
          {/* Form */}
          <div>
            <div className="eyebrow">{t("contactTitle")}</div>
            <h2 className="h-lg" style={{ marginBottom: 10 }}>
              {lang === "hi"
                ? "हमें संदेश भेजें"
                : lang === "gu"
                  ? "સંદેશ મોકલો"
                  : "Send us a message"}
            </h2>
            <div className="divider" style={{ marginBottom: 28 }} />

            {done ? (
              <div
                style={{
                  background: "var(--foam)",
                  border: "1px solid var(--mint)",
                  borderRadius: 18,
                  padding: "44px 28px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 66,
                    height: 66,
                    borderRadius: "50%",
                    background: "#dcfce7",
                    border: "2px solid #bbf7d0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 18px",
                    color: "#16a34a",
                  }}
                >
                  <Icons.Check size={28} />
                </div>
                <h4
                  style={{
                    fontFamily: "var(--serif)",
                    fontSize: 22,
                    color: "var(--pine)",
                    marginBottom: 8,
                  }}
                >
                  {lang === "hi"
                    ? "संदेश भेज दिया!"
                    : lang === "gu"
                      ? "સંદેશ મોકલ્યો!"
                      : "Message Sent!"}
                </h4>
                <p
                  style={{
                    color: "var(--stone)",
                    fontSize: 14,
                    marginBottom: 20,
                  }}
                >
                  {t("msgSent")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setForm({ name: "", phone: "", email: "", message: "" })
                  }
                >
                  {t("sendAnother") || "Send Another"}
                </Button>
              </div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 15 }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 14,
                  }}
                >
                  <Input
                    label={t("name")}
                    name="name"
                    value={form.name}
                    onChange={hc("name")}
                    placeholder="e.g. Ramesh Patel"
                    required
                    error={errors.name}
                  />
                  <Input
                    label={t("phone")}
                    name="phone"
                    value={form.phone}
                    onChange={hc("phone")}
                    placeholder="+91 XXXXX XXXXX"
                    required
                    type="tel"
                    error={errors.phone}
                  />
                </div>
                <Input
                  label={t("email")}
                  name="email"
                  value={form.email}
                  onChange={hc("email")}
                  placeholder="your@email.com"
                  type="email"
                />
                <Input
                  as="textarea"
                  label={t("message")}
                  name="message"
                  value={form.message}
                  onChange={hc("message")}
                  rows={5}
                  placeholder={
                    lang === "hi"
                      ? "हम कैसे मदद कर सकते हैं…"
                      : lang === "gu"
                        ? "અમે કઈ રીતે મદદ કરી શકીએ…"
                        : "Tell us how we can help…"
                  }
                />
                <div style={{ display: "flex", gap: 10 }}>
                  <Button
                    variant="primary"
                    size="lg"
                    loading={loading}
                    rightIcon={<Icons.Arrow size={15} />}
                    onClick={handleSubmit}
                    style={{ flex: 1, justifyContent: "center" }}
                  >
                    {t("send")}
                  </Button>
                  <Button
                    variant="whatsapp"
                    size="lg"
                    onClick={() => window.open(`https://wa.me/${cfg.whatsapp}`)}
                  >
                    💬
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="eyebrow">
              {lang === "hi"
                ? "संपर्क जानकारी"
                : lang === "gu"
                  ? "સંપર્ક માહિતી"
                  : "Contact Info"}
            </div>
            <h2 className="h-lg" style={{ marginBottom: 10 }}>
              {lang === "hi"
                ? "सीधे संपर्क करें"
                : lang === "gu"
                  ? "સીધો સંપર્ક"
                  : "Get in touch directly"}
            </h2>
            <div className="divider" style={{ marginBottom: 28 }} />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 24,
              }}
            >
              {INFO.map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 14,
                    padding: "16px 18px",
                    background: "#fff",
                    border: "1px solid rgba(0,0,0,.07)",
                    borderRadius: 12,
                    boxShadow: "var(--shadow-sm)",
                    transition: "all .25s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--mint)";
                    e.currentTarget.style.boxShadow = "var(--shadow-md)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(0,0,0,.07)";
                    e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: "var(--foam)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--pine)",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 1.2,
                        textTransform: "uppercase",
                        color: "var(--stone)",
                        marginBottom: 3,
                      }}
                    >
                      {item.label}
                    </div>
                    {/* Contact values switch language */}
                    <div
                      style={{
                        fontSize: 14,
                        color: "var(--ink)",
                        lineHeight: 1.7,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {item.val}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                background: "var(--foam)",
                borderRadius: 14,
                height: 180,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "1.5px dashed var(--mint)",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 32 }}>🗺️</div>
              <div
                style={{ fontWeight: 600, color: "var(--pine)", fontSize: 15 }}
              >
                {cfg.mapLabel}
              </div>
              <a
                href={`https://maps.google.com/?q=${cfg.mapQuery}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  fontSize: 13,
                  color: "var(--emerald)",
                  fontWeight: 600,
                }}
              >
                {t("openMaps") || "Open in Google Maps ↗"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// SIMPLE PAGE (Privacy / Terms)
// ═════════════════════════════════════════════════════════════════════════════
export const SimplePage = ({ title, content }) => (
  <div style={{ paddingBottom: 72 }}>
    <PageHeader title={title} crumb={["Home", title]} />
    <div className="wrap" style={{ paddingTop: 52 }}>
      <div
        style={{
          maxWidth: 740,
          margin: "0 auto",
          background: "#fff",
          border: "1px solid rgba(0,0,0,.07)",
          borderRadius: 18,
          padding: "clamp(22px,4vw,48px)",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        <div
          style={{
            fontSize: 15,
            lineHeight: 1.95,
            color: "var(--stone)",
            fontWeight: 300,
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    </div>
  </div>
);
